// ABOUTME: TypeScript type definitions for SharePoint API, aligned with backend types
// ABOUTME: All types use 'type' instead of 'interface' and follow immutability principles

// Core SharePoint item types - aligned with backend Item type
export type ISharePointFile = {
    readonly id?: string;
    readonly driveItemId: string;
    readonly name: string;
    readonly type: 'file';
    readonly size: number;
    readonly modified: string;
    readonly webUrl: string;
    readonly mimeType: string;
    readonly breadcrumbs: readonly IItemBreadcums[];
    readonly cTagParsed: string;
    readonly parentReference: {
        readonly id: string;
    };
};

export type ISharePointFolder = {
    readonly id?: string;
    readonly driveItemId: string;
    readonly name: string;
    readonly type: 'folder';
    readonly childCount: number;
    readonly modified: string;
    readonly webUrl: string;
    readonly breadcrumbs: readonly IItemBreadcums[];
    readonly cTagParsed: string;
    readonly size: number;
    readonly parentReference: {
        readonly id: string;
    };
};

export type ISharePointItem = ISharePointFile | ISharePointFolder;

export type IItemBreadcums = {
    readonly driveItemId: string;
    readonly name: string;
};

export type IBrowseRequest = {
    readonly hierarchyId: string;
    readonly listId?: string;
    readonly driveItemId?: string;
    readonly pageSize?: number;
    readonly nextLink?: string;
};

export type IBrowseResult = {
    readonly items: readonly ISharePointItem[];
    readonly driveItemId: string;
    readonly breadcrumbs: readonly IItemBreadcums[];
    readonly nextLink?: string;
    readonly hasMore: boolean;
    readonly totalCount?: number;
};

export type IBrowseResponse = {
    readonly success: boolean;
    readonly data?: IBrowseResult;
    readonly error?: string;
    readonly message?: string;
};

// Search API types - aligned with backend SearchReq/SearchRes
export type ISearchRequest = {
    readonly hierarchyId: string;
    readonly query: string;
    readonly pageSize?: number;
    readonly nextLink?: string;
};

export type ISearchResult = {
    readonly items: readonly ISharePointItem[];
    readonly query: string;
    readonly totalCount: number;
    readonly nextLink?: string;
    readonly hasMore: boolean;
};

export type ISearchResponse = {
    readonly success: boolean;
    readonly data?: ISearchResult;
    readonly error?: string;
    readonly message?: string;
};

// Items API types - aligned with backend ItemsReq/ItemsRes
export type IItemsRequest = {
    readonly hierarchyId: string;
    readonly cTagParsedIds: string[];
};

export type IItemsResponse = {
    readonly success: boolean;
    readonly data?: ISharePointItem[];
    readonly error?: string;
    readonly message?: string;
};

// GenerateNodes API types - aligned with backend GenerateNodesReq/GenerateNodesRes
export type IGenerateNodesRequest = {
    readonly hierarchyId: string;
    readonly items: ISharePointItem[];
};

export type IGenerateNodesResult = {
    nodeCreated?: number;
    nodeContentUpdated?: number;
    notModifiedItems: number;
    errors: number;
};
export type IGenerateNodesResponse = {
    readonly success: boolean;
    readonly data?: IGenerateNodesResult;
    readonly error?: string;
    readonly message?: string;
};

// Utility type for API error handling
export type IApiError = {
    readonly message: string;
    readonly code?: string;
    readonly details?: unknown;
};

// Type guards for SharePoint item discrimination
export const isSharePointFile = (item: ISharePointItem): item is ISharePointFile =>
    item.type === 'file';

export const isSharePointFolder = (item: ISharePointItem): item is ISharePointFolder =>
    item.type === 'folder';
